// services/auth/logout.js

import { cacheManager } from '@/services/app/CacheManager';
import { crossTabManager } from '@/services/app/CrossTabManager';
import { logger } from '@/utils/logger';
import { getKeycloakUrl } from '@/services/auth/keycloak-url';

const log = logger.auth;

/**
 * Enhanced Logout Service
 * 
 * Properly handles all aspects of logout WITHOUT Keycloak redirects:
 * 1. Direct API calls for backend session termination
 * 2. Frontend state cleanup
 * 3. Cross-tab coordination
 * 4. No redirect to auth.supplier-s.io
 * 
 * FIXED: Removed keycloak.logout() to prevent redirects after reload
 * IMPORTANT: very badly described, trust me, its complicated with the current architecture middleware <> frontend <> keycloak
 */

class LogoutService {
  constructor() {
    this.logoutInProgress = false;
    this.logoutAttempts = 0;
    this.maxLogoutAttempts = 3;
  }

  /**
   * Main logout method - orchestrates the entire logout flow
   */
  async logout(options = {}) {
    const {
      reason = 'user_initiated',
      isInitiator = true,
      skipNavigation = false
    } = options;

    // Prevent concurrent logout attempts
    if (this.logoutInProgress) {
      log.log('Logout already in progress');
      return { success: true, reason: 'already_in_progress' };
    }

    this.logoutInProgress = true;
    this.logoutAttempts++;

    try {
      log.log(`Starting logout (reason: ${reason})`);

      // Step 1: Set logout markers FIRST
      this.setLogoutMarkers(reason, isInitiator);

      // Step 2: Notify other tabs (with cleanup of old events)
      if (isInitiator) {
        this.notifyOtherTabs();
      }

      // Step 3: Terminate backend session (FIXED: No Keycloak redirect)
      const backendLogoutSuccess = await this.terminateBackendSession();

      // Step 4: Clear frontend state (regardless of backend success)
      const frontendClearSuccess = await this.clearFrontendState();

      // Step 5: Clear WebSocket connections
      this.clearWebSocketConnections();

      // Step 6: Navigate to logout page (unless skipped)
      if (!skipNavigation && typeof window !== 'undefined') {
        // Small delay to ensure cleanup completes
        setTimeout(() => {
          window.location.href = '/logout/';
        }, 100);
      }

      log.log('Logout completed successfully');
      return {
        success: true,
        backendLogout: backendLogoutSuccess,
        frontendClear: frontendClearSuccess
      };

    } catch (error) {
      log.error('Logout error:', error);

      // Even on error, try to clear frontend state
      try {
        await this.clearFrontendState();
      } catch (cleanupError) {
        log.error('Cleanup error:', cleanupError);
      }

      return {
        success: false,
        error: error.message
      };

    } finally {
      this.logoutInProgress = false;
    }
  }

  /**
   * Set logout markers for coordination
   */
  setLogoutMarkers(reason, isInitiator) {
    try {
      // Session markers
      sessionStorage.setItem('logoutReason', reason);
      sessionStorage.setItem('controlledLogout', 'true');

      if (isInitiator) {
        sessionStorage.setItem('logout_initiator', 'true');
      }

      // Timestamp for tracking
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());

    } catch (error) {
      log.error('Error setting logout markers:', error);
    }
  }

  /**
   * Notify other tabs about logout (with event cleanup)
   */
  notifyOtherTabs() {
    try {
      // First, clean up any stale logout events
      this.cleanupStaleLogoutEvents();

      // Use CrossTabManager to notify other tabs
      crossTabManager.emitLogout('user_initiated');

      // Also remove cross-tab auth immediately
      localStorage.removeItem('crossTabAuthInfo');

      // Clear rebrand-cleanup gate keys for cross-tab consistency. Critical
      // for the shared-device case: user A logs out → user B logs in → B
      // must hit the gate fresh and see announcements fresh, not inherit
      // A's cache. Also sweep any legacy relaunchSeen_* / relaunchCheckTimestamp
      // stragglers left over from the pre-cleanup release.
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith('termsAccepted_') ||
            key.startsWith('announcement_state_') ||
            key === 'announcement_check_at' ||
            // Legacy
            key.startsWith('relaunchSeen_') ||
            key === 'relaunchCheckTimestamp')
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Ignore individual removal errors
        }
      });

      // Session-scoped Terms gate Later flags — sweep so user B logging in
      // after A on the same tab doesn't inherit A's deferral.
      try {
        const sessionKeys = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.startsWith('termsLaterDeferred_')) sessionKeys.push(k);
        }
        sessionKeys.forEach(k => sessionStorage.removeItem(k));
      } catch (e) {
        // Ignore
      }

    } catch (error) {
      log.error('Error notifying other tabs:', error);
    }
  }

  /**
   * Clean up stale logout events (older than 10 seconds)
   * Cleans both legacy 'logout_event' and new 'crossTab_logout' keys
   */
  cleanupStaleLogoutEvents() {
    // Clean legacy logout_event key
    try {
      const existingEvent = localStorage.getItem('logout_event');
      if (existingEvent) {
        const event = JSON.parse(existingEvent);
        if (Date.now() - event.timestamp > 10000) {
          log.log('Removing stale logout event');
          localStorage.removeItem('logout_event');
        }
      }
    } catch (error) {
      // Ignore parse errors, just remove it
      localStorage.removeItem('logout_event');
    }

    // CrossTabManager handles its own cleanup via cleanupStaleEvents()
  }

  /**
   * Terminate backend session with Keycloak
   * FIXED: Removed keycloak.logout() to prevent redirects
   */
  async terminateBackendSession() {
    try {
      log.log('Terminating backend session');

      // Get auth service reference
      const authService = window.authService ||
        (await import('./index')).authService;

      if (!authService) {
        log.warn('Auth service not available');
        return false;
      }

      // Get Keycloak instance for tokens only
      const keycloak = authService.keycloak ||
        authService.keycloakAuthService?.keycloak;

      // Collect all tokens
      const tokens = {
        accessToken: keycloak?.token || authService.state?.token,
        refreshToken: keycloak?.refreshToken,
        idToken: keycloak?.idToken
      };

      // CRITICAL: If no idToken in memory, try to get from cache
      if (!tokens.idToken) {
        try {
          const tokenStateJson = sessionStorage.getItem('keycloakTokenState');
          if (tokenStateJson) {
            const tokenState = JSON.parse(tokenStateJson);
            tokens.idToken = tokenState.idToken;
            log.log('Got idToken from cache');
          }
        } catch (e) {
          log.log('Could not get idToken from cache:', e);
        }
      }

      let logoutSuccess = false;

      // Direct API call approach
      if (tokens.refreshToken || tokens.accessToken) {
        logoutSuccess = await this.callLogoutEndpoint(tokens);
      }

      // Try token revocation as fallback
      if (!logoutSuccess && tokens.refreshToken) {
        logoutSuccess = await this.revokeTokens(tokens);
      }

      // If we have no tokens, consider it a success (nothing to logout)
      if (!tokens.accessToken && !tokens.refreshToken) {
        log.log('No tokens to logout, considering success');
        logoutSuccess = true;
      }

      return logoutSuccess;

    } catch (error) {
      log.error('Backend logout error:', error);
      return false;
    }
  }

  /**
   * Call the backend logout endpoint, which ends the Keycloak SSO session
   * server-side (OIDC logout needs the client secret, which stays there).
   */
  async callLogoutEndpoint(tokens) {
    try {
      const logoutUrl = `${process.env.GATSBY_STRAPI_URL}/api/auth/logout`;

      const response = await fetch(logoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          idToken: tokens.idToken,
          // Hostname-aware: the backend must call the same auth host this
          // session was minted under or Keycloak rejects the logout
          issuer: getKeycloakUrl()
        })
      });

      if (response.ok || response.status === 204) {
        log.log('Logout endpoint called successfully');
        return true;
      }

      log.warn(`Logout endpoint returned ${response.status}`);
      return false;

    } catch (error) {
      log.error('Logout endpoint error:', error);
      return false;
    }
  }

  /**
   * Revoke tokens as a fallback
   */
  async revokeTokens(tokens) {
    try {
      const keycloakUrl = getKeycloakUrl();
      const realm = process.env.GATSBY_KEYCLOAK_REALM || 'platform';
      const clientId = process.env.GATSBY_KEYCLOAK_CLIENT_ID || 'platform';

      const revokeUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/revoke`;

      // Revoke refresh token
      if (tokens.refreshToken) {
        const formData = new URLSearchParams();
        formData.append('client_id', clientId);
        formData.append('token', tokens.refreshToken);
        formData.append('token_type_hint', 'refresh_token');

        const response = await fetch(revokeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData
        });

        if (response.ok || response.status === 200) {
          log.log('Refresh token revoked');
          return true;
        }
      }

      return false;

    } catch (error) {
      log.error('Token revocation error:', error);
      return false;
    }
  }

  /**
   * Clear all frontend state
   * Uses CacheManager.clearAll() which includes rebrand-cleanup gate cleanup
   */
  async clearFrontendState() {
    try {
      log.log('Clearing frontend state');

      // Shutdown Intercom widget FIRST (critical for security)
      try {
        if (typeof window !== 'undefined' && window.intercomService) {
          const shutdownSuccess = window.intercomService.shutdown();
          log.log('Intercom shutdown:', shutdownSuccess ? 'success' : 'skipped');
        }
      } catch (intercomError) {
        log.error('Intercom shutdown error:', intercomError);
      }

      // Use CacheManager to clear all cache
      // This now includes rebrand-cleanup gate cleanup
      cacheManager.clearAll();

      // Clear auth service state
      if (window.authService) {
        if (window.authService.state) {
          window.authService.state = {
            initialized: false,
            authenticated: false,
            token: null,
            userProfile: null,
            tokenExpiry: null
          };
        }

        // Clear Keycloak state
        const keycloak = window.authService.keycloak ||
          window.authService.keycloakAuthService?.keycloak;

        if (keycloak) {
          keycloak.authenticated = false;
          keycloak.token = null;
          keycloak.refreshToken = null;
          keycloak.idToken = null;
          keycloak.tokenParsed = null;
          keycloak.refreshTokenParsed = null;
          keycloak.idTokenParsed = null;
        }
      }

      // Clear app store state
      if (window.appStore && window.appStore.clearState) {
        await window.appStore.clearState();
      }

      // Clear any remaining session markers (except logout markers)
      const logoutMarkers = [
        'logoutReason',
        'controlledLogout',
        'logout_initiator',
        'logoutTimestamp'
      ];

      // Get all sessionStorage keys
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && !logoutMarkers.includes(key)) {
          keysToRemove.push(key);
        }
      }

      // Remove non-logout keys
      keysToRemove.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Continue
        }
      });

      log.log('Frontend state cleared');
      return true;

    } catch (error) {
      log.error('Error clearing frontend state:', error);
      return false;
    }
  }

  /**
   * Clear WebSocket connections
   */
  clearWebSocketConnections() {
    try {
      if (window.appStore?.webSocketHandler) {
        const handler = window.appStore.webSocketHandler;

        // Close socket and timers
        handler.cleanupConnection();

        // Reset instance state to prevent leakage between accounts
        handler.connected = false;
        if (handler.processedEvents) handler.processedEvents.clear();
        if (handler.handlers) handler.handlers.clear();
        handler.reconnectAttempts = 0;
        handler.lastCalculationStatus = { timestamp: null, projectId: null, inProgress: false };
      }
    } catch (error) {
      log.error('Error clearing WebSocket:', error);
    }
  }

  /**
   * Handle cross-tab logout event
   */
  async handleCrossTabLogout(event) {
    if (!event || event.type !== 'logout_event') {
      return;
    }

    // Check if event is stale
    if (Date.now() - event.timestamp > 10000) {
      log.log('Ignoring stale logout event');
      return;
    }

    // Check if we're already on logout page
    if (window.location.pathname === '/logout/') {
      log.log('Already on logout page');
      return;
    }

    // Mark as cross-tab logout
    sessionStorage.setItem('crossTabLogout', 'true');

    // Perform logout without initiating new cross-tab event
    await this.logout({
      reason: 'cross_tab',
      isInitiator: false,
      skipNavigation: false
    });
  }

  /**
   * Check if logout is needed (for session timeout, etc.)
   */
  isLogoutNeeded() {
    // Check if we're in controlled logout state
    const controlledLogout = sessionStorage.getItem('controlledLogout') === 'true';
    if (controlledLogout) {
      return false; // Already logging out
    }

    // Check if auth is valid
    const authState = cacheManager.getAuthState();
    if (!authState || !authState.token) {
      return true; // No valid auth
    }

    // Check token expiry
    if (authState.tokenExpiry && Date.now() >= authState.tokenExpiry) {
      return true; // Token expired
    }

    return false;
  }
}

// Export singleton instance
export const logoutService = new LogoutService();

// Also export class for testing
export { LogoutService };